// Scales a picture down in the browser before it is uploaded, so the server never
// receives multi-megabyte camera originals. The longest side is capped, the aspect
// ratio is kept, and the result is always a JPEG drawn on a white background: the
// pictures are shown on white cards, so transparency has nothing to offer. Anything
// that is not a JPEG or PNG, or that cannot be decoded, is returned untouched and
// left for the backend to judge.
const MAX_SIDE = 1280;
const JPEG_QUALITY = 0.8;
// Same limit as the backend: a JPEG that already fits is not re-encoded.
const MAX_BYTES_UNTOUCHED = 2 * 1024 * 1024;
const JPEG_TYPES = ["image/jpeg", "image/jpg"];
const RESIZABLE_TYPES = [...JPEG_TYPES, "image/png"];

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image could not be decoded"));
    };
    image.src = url;
  });

const toBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const replaceExtension = (fileName, extension) => {
  const lastDot = fileName.lastIndexOf(".");
  return (lastDot >= 0 ? fileName.substring(0, lastDot) : fileName) + extension;
};

export const resizeImageForUpload = async (
  file,
  { maxSide = MAX_SIDE, jpegQuality = JPEG_QUALITY } = {},
) => {
  if (!file || !RESIZABLE_TYPES.includes(file.type)) return file;

  let image;
  try {
    image = await loadImage(file);
  } catch {
    return file;
  }

  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const alreadyFits =
    JPEG_TYPES.includes(file.type) &&
    scale === 1 &&
    file.size <= MAX_BYTES_UNTOUCHED;
  if (alreadyFits) return file;

  const width = Math.round(image.width * scale);
  const height = Math.round(image.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  // JPEG has no alpha channel; without this, transparent areas would turn black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const blob = await toBlob(canvas, "image/jpeg", jpegQuality);
  if (!blob) return file;

  return new File([blob], replaceExtension(file.name, ".jpg"), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};
